import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { FsComboOption } from '../../common/fs-combobox/fs-combobox.component';
import { FsReportMember, FsReportTarget } from '../../common/fs-report-download/fs-report-download.component';
import { Participant, SessionGroup, SplitType } from '../../invitations/models/invitation.model';
import { InvitationMockService } from '../../invitations/services/invitation-mock.service';
import { Session, SPLIT_TYPE_LABELS } from '../models/session.model';
import { SessionMockService } from '../services/session-mock.service';

/**
 * The standalone session board page (`/sessions/:id`) — the Task-9 replacement for the
 * invitation-embedded `SessionPanelComponent`. Same board/downloads/re-split lifecycle, but driven
 * by the route id against `SessionMockService` instead of an `Invitation`'s embedded session, and
 * resolving group members via `InvitationMockService.participantsByIds` since a session's
 * participants are referenced across invitations, not owned by one.
 */
@Component({
  selector: 'app-session-board-page',
  templateUrl: './session-board-page.component.html',
  styleUrls: ['./session-board-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionBoardPageComponent implements OnInit {
  session: Session | null = null;
  /** Resolved, in participantIds order — the board never looks ids up itself. */
  members: Participant[] = [];
  /** An array, not a Set — Angular does not track Set mutations, and the board takes it as @Input. */
  selectedIds: string[] = [];

  modalMode: 'resplit' | 'edit' | null = null;
  downloadTarget: FsReportTarget | null = null;
  /** The combined "Download session ▾" dropdown — same open/close pattern as invitation-detail's. */
  dlOpen = false;

  constructor(
    private svc: SessionMockService,
    private invSvc: InvitationMockService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.svc.getById(id).subscribe(s => {
      if (!s) { this.router.navigate(['/activity-groups']); return; }
      this.setSession(s);
    });
  }

  private setSession(s: Session): void {
    this.session = s;
    this.members = this.invSvc.participantsByIds(s.participantIds);
    // Drop selections for anyone no longer resolvable (e.g. a group was deleted/re-split).
    this.selectedIds = this.selectedIds.filter(id => this.members.some(m => m.id === id));
    // Same for group selection: a re-split mints new group ids, so stale ones must not survive.
    this.selectedGroupIds = this.selectedGroupIds.filter(id => s.groups.some(g => g.id === id));
  }

  private apply(obs: Observable<Session>): void {
    obs.subscribe(s => this.setSession(s));
  }

  get expired(): boolean { return !!this.session && this.svc.isExpired(this.session); }

  /** Options for the "Move to group" keyboard control in the selection bar. */
  get groupOptions(): FsComboOption[] {
    return this.session?.groups.map(g => ({ id: g.id, name: g.name })) ?? [];
  }

  /** Human label for the header's split-type badge (matches sessions-list's mapping). */
  splitLabel(t: SplitType): string { return SPLIT_TYPE_LABELS[t] ?? t; }

  // --- session lifecycle ---
  confirmModal(req: { name: string; expiresAt?: string; splitType: SplitType; groupCount: number }): void {
    const mode = this.modalMode;
    this.modalMode = null;
    if (!this.session) { return; }
    if (mode === 'resplit') {
      this.apply(this.svc.resplit(this.session.id, { splitType: req.splitType, groupCount: req.groupCount }));
    } else if (mode === 'edit') {
      this.apply(this.svc.update(this.session.id, { name: req.name, expiresAt: req.expiresAt }));
    }
  }

  deleteSession(): void {
    if (!this.session) { return; }
    const id = this.session.id;
    Swal.fire({
      title: 'Delete this activity group?',
      text: 'This permanently removes the activity group and its groups. This cannot be undone.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#f54359',
    }).then(r => {
      if (r.isConfirmed) { this.svc.remove(id).subscribe(() => this.router.navigate(['/activity-groups'])); }
    });
  }

  // --- board wiring ---
  onMove(e: { memberId: string; toGroupId: string; toIndex: number }): void {
    if (!this.session) { return; }
    this.apply(this.svc.moveMember(this.session.id, e.memberId, e.toGroupId, e.toIndex));
  }
  onRenameGroup(e: { groupId: string; name: string }): void {
    if (!this.session) { return; }
    this.apply(this.svc.renameGroup(this.session.id, e.groupId, e.name));
  }
  onDeleteGroup(groupId: string): void {
    if (!this.session) { return; }
    this.apply(this.svc.deleteGroup(this.session.id, groupId));
  }
  addGroup(): void {
    if (!this.session) { return; }
    this.apply(this.svc.addGroup(this.session.id));
  }

  // --- selection ---
  toggleMember(id: string): void {
    this.selectedIds = this.selectedIds.includes(id)
      ? this.selectedIds.filter(x => x !== id)
      : [...this.selectedIds, id];
  }
  selectGroup(groupId: string): void {
    const ids = this.session?.groups.find(g => g.id === groupId)?.memberIds ?? [];
    this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
  }
  clearSelection(): void { this.selectedIds = []; }

  // --- group-level selection ---
  // Independent of participant selection: you pick groups to act on whole groups, participants to
  // act on people. Both bars can be open at once; each clears only its own axis.
  selectedGroupIds: string[] = [];

  toggleGroupSelect(groupId: string): void {
    this.selectedGroupIds = this.selectedGroupIds.includes(groupId)
      ? this.selectedGroupIds.filter(id => id !== groupId)
      : [...this.selectedGroupIds, groupId];
  }
  clearGroupSelection(): void { this.selectedGroupIds = []; }

  /** Selected groups in board order, so the download title reads the way the board looks. */
  private selectedGroups(): SessionGroup[] {
    return (this.session?.groups ?? []).filter(g => this.selectedGroupIds.includes(g.id));
  }

  /**
   * Download the selected groups' reports. The report target is a flat member list, so this
   * mirrors the single-group menu action with a title naming the groups — producing genuinely
   * per-group documents is the reporting API's job, not something the mock can fake.
   */
  downloadSelectedGroups(): void {
    const groups = this.selectedGroups();
    if (!groups.length) { return; }
    const memberIds = [...new Set(groups.flatMap(g => g.memberIds))];
    this.downloadTarget = {
      title: groups.length === 1 ? groups[0].name : `${groups.length} groups`,
      members: this.toReportMembers(memberIds),
      live: false,
      // Group-scoped, so the CSV is a real group list: every member carries its group name.
      allowCsv: true,
    };
  }

  /**
   * Keyboard path for moving participants between groups. Drag-and-drop on the board has no
   * keyboard equivalent, so the selection bar's "Move to group" combobox (keyboard-complete —
   * arrows/Enter/Escape) is retained as the accessible alternative, per the sessions spec.
   * Moves every selected member, then clears the selection. Emits once, with the final state.
   */
  moveSelectionToGroup(toGroupId: string | null): void {
    if (!toGroupId || !this.selectedIds.length || !this.session) { return; }
    const sessionId = this.session.id;
    let last: Session | undefined;
    // N sequential moveMember calls, each reading the previous one's committed state, is only
    // correct because SessionMockService.mutate() is synchronous (it clones subject.value,
    // commits, and returns `of(session)` — the subscribe callback runs before the next iteration
    // starts). If the service ever became genuinely async, these calls would race and need to be
    // chained (e.g. via concatMap) instead of fired in a loop.
    this.selectedIds.forEach(memberId => {
      this.svc.moveMember(sessionId, memberId, toGroupId, 0).subscribe(s => { last = s; });
    });
    this.clearSelection();
    if (last) { this.setSession(last); }
  }

  // --- downloads: the three scopes are just three different member lists ---
  private toReportMembers(ids: string[]): FsReportMember[] {
    return ids.map((id): FsReportMember | undefined => {
      const p = this.members.find(x => x.id === id);
      const group = this.session?.groups.find(g => g.memberIds.includes(id));
      return p && {
        id: p.id, firstName: p.firstName, lastName: p.lastName,
        groupName: group?.name, profileTypeId: p.profileTypeId,
      };
    }).filter((m): m is FsReportMember => !!m);
  }
  downloadSession(): void {
    this.dlOpen = false;
    const ids = this.session?.groups.flatMap(g => g.memberIds) ?? [];
    this.downloadTarget = { title: this.session?.name ?? 'Session', members: this.toReportMembers(ids), live: false, allowCsv: true };
  }
  downloadGroup(groupId: string): void {
    const g = this.session?.groups.find(x => x.id === groupId);
    if (!g) { return; }
    this.downloadTarget = { title: g.name, members: this.toReportMembers(g.memberIds), live: false, allowCsv: true };
  }
  downloadSelection(): void {
    this.downloadTarget = {
      title: `${this.selectedIds.length} selected`,
      members: this.toReportMembers(this.selectedIds),
      live: false,
      allowCsv: true,
    };
  }
}
